// src/hooks/clientes/use-cliente-form.ts
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ClienteSegmento } from "@workflow/types";

import { ClienteDTO } from "@/types/cliente.type";
import { useClienteActions } from "./use-cliente-actions";

export const clienteValidationSchema = z.object({
  nome: z.string().min(3, "O nome deve ter no mínimo 3 caracteres."),
  grupo: z.string().optional(),
  segmento: z.union([z.nativeEnum(ClienteSegmento), z.literal("")]).optional(),
  descricao: z.string().optional(),
});

export type ClienteFormValues = z.infer<typeof clienteValidationSchema>;

// HOOK DE CRIAÇÃO
export function useClienteCreateForm(open: boolean, onOpenChange: (open: boolean) => void) {
  const { createCliente, isCreating } = useClienteActions();

  const form = useForm<ClienteFormValues>({
    resolver: zodResolver(clienteValidationSchema),
    mode: "all",
    defaultValues: {
      nome: "",
      grupo: "",
      segmento: "",
      descricao: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        nome: "",
        grupo: "",
        segmento: "",
        descricao: "",
      });
    }
  }, [open, form]);

  const onSubmit = async (values: ClienteFormValues) => {
    try {
      if (!values.nome) {
        throw new Error("Grupo é obrigatório");
      }

      await createCliente({
        nome: values.nome,
        grupo: values.grupo,
        segmento: values.segmento || undefined,
        descricao: values.descricao || undefined,
      });
      onOpenChange(false);
    } catch (error) {
      console.error("Erro ao criar cliente:", error);
    }
  };

  return { form, isSubmitting: isCreating, onSubmit: form.handleSubmit(onSubmit) };
}

// HOOK DE EDIÇÃO
export function useClienteEditForm(
  open: boolean,
  onOpenChange: (open: boolean) => void,
  clienteToEdit: ClienteDTO | null,
  onSuccess: (dadosAtualizados: Partial<ClienteDTO>) => void,
) {
  const { updateCliente, isUpdating } = useClienteActions();

  const form = useForm<ClienteFormValues>({
    resolver: zodResolver(clienteValidationSchema),
    mode: "all",
    defaultValues: {
      nome: "",
      grupo: "",
      segmento: "",
      descricao: "",
    },
  });

  useEffect(() => {
    if (open && clienteToEdit) {
      form.reset({
        nome: clienteToEdit.nome,
        grupo: clienteToEdit.grupo || "",
        segmento: clienteToEdit.segmento || "",
        descricao: clienteToEdit.descricao || "",
      });
    }
  }, [open, clienteToEdit, form]);

  const onSubmit = async (values: ClienteFormValues) => {
    if (!clienteToEdit) return;
    try {
      const data = {
        nome: values.nome,
        grupo: values.grupo,
        segmento: values.segmento || null,
        descricao: values.descricao || undefined,
      };
      await updateCliente({ id: clienteToEdit.id, data });
      onSuccess({ ...clienteToEdit, ...data });
      onOpenChange(false);
    } catch (error) {
      console.error("Erro ao atualizar cliente:", error);
    }
  };

  return { form, isSubmitting: isUpdating, onSubmit: form.handleSubmit(onSubmit) };
}
